<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  // Store
  const restaurantStore = useRestaurantStore();

  // Props
  const props = defineProps<{
    title: string;
    value: string | number;
    change?: number;
    loading?: boolean;
    icon?: string;
    to?: string;
    isCurrency?: boolean;
    changeLabel?: string;
    /**
     * Data-quality caveat: renders an info icon next to the value with this
     * text as its tooltip.
     */
    valueInfo?: string;
    /**
     * Color of the valueInfo icon. 'warning' highlights it (amber) when the
     * caveat carries excluded amounts.
     */
    valueInfoColor?: 'gray' | 'warning';
    /**
     * Teaching copy shown instead of the value and the change (the page
     * passes it only while the underlying number is 0).
     */
    emptyText?: string;
    /** A button under `emptyText`; emits `empty-action`. */
    emptyActionLabel?: string;
  }>();

  const emit = defineEmits<{
    (e: 'empty-action'): void;
  }>();

  // Computed
  const changeColor = computed(() => {
    if (props.change === undefined) return 'grey';
    if (props.change === 0) return 'blue'; // New neutral state
    return props.change > 0 ? 'success' : 'error';
  });

  const changeIcon = computed(() => {
    if (props.change === undefined) return '';
    if (props.change === 0) return 'mdi-equal'; // New neutral icon
    return props.change > 0 ? 'mdi-trending-up' : 'mdi-trending-down';
  });

  const changeText = computed(() => {
    if (props.change === undefined) return '';
    const prefix = props.change > 0 ? '+' : '';
    if (props.isCurrency) {
      return `${prefix}${restaurantStore.currency?.symbol || '$'}${Math.abs(
        props.change
      ).toFixed(2)}`;
    }
    return `${prefix}${props.change}`;
  });

  const showEmpty = computed(() => !!props.emptyText && !props.loading);

  // A button cannot live inside a link: while it shows, the card is not one.
  const cardTo = computed(() =>
    showEmpty.value && props.emptyActionLabel ? undefined : props.to
  );

  const formattedValue = computed(() => {
    if (typeof props.value === 'number' && !props.isCurrency) {
      return props.value.toLocaleString();
    }
    return props.value;
  });
</script>

<template>
  <v-card class="metric-card" :to="cardTo" :hover="!!cardTo" elevation="0">
    <v-card-text class="card-content">
      <!-- Icon -->
      <div class="icon-container">
        <v-skeleton-loader
          v-if="loading"
          type="avatar"
          width="48"
          height="48"
          class="icon-skeleton"
        />
        <v-icon
          v-else
          :color="icon ? 'orange' : 'grey'"
          size="32"
          class="metric-icon"
        >
          {{ icon || 'mdi-chart-line' }}
        </v-icon>
      </div>

      <!-- Content -->
      <div class="metric-content">
        <!-- Title -->
        <v-skeleton-loader
          v-if="loading"
          type="text"
          width="100"
          class="mb-1"
        />
        <h4 v-else class="metric-title">
          {{ title }}
        </h4>

        <!-- Value -->
        <v-skeleton-loader
          v-if="loading"
          type="heading"
          width="80"
          class="mb-1"
        />
        <div v-else-if="showEmpty" class="metric-empty">
          <p class="empty-text">{{ emptyText }}</p>
          <PosUiButton
            v-if="emptyActionLabel"
            class="empty-action"
            color="neutral"
            variant="outline"
            size="sm"
            icon="share-variant-outline"
            @click="emit('empty-action')"
            >{{ emptyActionLabel }}</PosUiButton
          >
        </div>
        <div v-else class="metric-value">
          <span class="value-text">{{ formattedValue }}</span>
          <v-tooltip v-if="valueInfo" bottom max-width="280">
            <template #activator="{ on, attrs }">
              <v-icon
                small
                class="value-info-icon"
                :class="{
                  'value-info-icon--warning': valueInfoColor === 'warning',
                }"
                v-bind="attrs"
                v-on="on"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>{{ valueInfo }}</span>
          </v-tooltip>
        </div>

        <!-- Change indicator -->
        <v-skeleton-loader v-if="loading" type="chip" width="60" />
        <div
          v-else-if="!showEmpty && change !== undefined"
          class="change-indicator"
        >
          <v-chip
            x-small
            :color="changeColor"
            text-color="white"
            class="change-chip"
          >
            <v-icon x-small left>{{ changeIcon }}</v-icon>
            {{ changeText }}
          </v-chip>
          <span class="change-label">{{
            changeLabel || $t('dashboard.vsLastWeek')
          }}</span>
        </div>
      </div>

      <!-- Arrow for clickable cards -->
      <v-icon v-if="cardTo && !loading" class="arrow-icon" color="grey">
        mdi-chevron-right
      </v-icon>
    </v-card-text>
  </v-card>
</template>

<style scoped lang="scss">
  .metric-card {
    @apply tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-neutral-100;
    @apply tw-transition-all tw-duration-200;

    &:hover {
      @apply tw-shadow-md tw-border-neutral-200;

      .arrow-icon {
        @apply tw-opacity-100;
      }
    }

    .card-content {
      @apply tw-flex tw-items-center tw-p-4 tw-space-x-4;
    }

    .icon-container {
      @apply tw-flex-shrink-0;

      .icon-skeleton {
        @apply tw-rounded-full;
      }

      .metric-icon {
        @apply tw-bg-neutral-50 tw-rounded-full tw-p-2;
        width: 48px;
        height: 48px;
      }
    }

    .metric-content {
      @apply tw-flex-grow tw-min-w-0;

      .metric-title {
        @apply tw-text-sm tw-font-medium tw-text-neutral-600 tw-mb-1;
        @apply tw-truncate;
      }

      .metric-value {
        @apply tw-flex tw-items-center tw-mb-1;

        .value-text {
          @apply tw-text-2xl tw-font-bold tw-text-neutral-900 tw-truncate;
        }

        .value-info-icon {
          // Icon margin instead of flex gap: unsupported on old POS devices
          // (Chrome <84).
          @apply tw-ml-1.5 tw-text-neutral-400 tw-cursor-help;

          &--warning {
            @apply tw-text-amber-500;
          }
        }
      }

      .metric-empty {
        @apply tw-flex tw-flex-col tw-items-start tw-gap-2;

        .empty-text {
          @apply tw-m-0 tw-text-sm tw-text-neutral-500;
        }

        // «sm» button, 44 px hit area (touch-first admin).
        .empty-action {
          @apply tw-min-h-11;
        }
      }

      .change-indicator {
        @apply tw-flex tw-items-center tw-space-x-2;

        .change-chip {
          @apply tw-font-medium tw-flex-shrink-0;
        }

        .change-label {
          @apply tw-text-xs tw-text-neutral-500 tw-truncate;
        }
      }
    }

    .arrow-icon {
      @apply tw-flex-shrink-0 tw-opacity-0 tw-transition-opacity tw-duration-200;
    }
  }

  // Responsive adjustments
  @media (max-width: 640px) {
    .metric-card {
      .metric-content {
        .metric-value {
          .value-text {
            @apply tw-text-xl;
          }
        }
      }
    }
  }
</style>
